import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { ROUTER_PATHS } from 'app/core/constants/router';
import { AuthSessionService } from 'app/core/services/auth-session.service';
import { Notification } from 'app/shared/services/notification';
import { isConfiguredApiRequest } from './api-request.util';

const DEFAULT_SERVER_ERROR_MESSAGE = 'Ocurrió un error en el servidor. Inténtalo más tarde.';
const DEFAULT_REQUEST_ERROR_MESSAGE = 'No fue posible completar la solicitud.';
const SESSION_EXPIRED_MESSAGE =
  'Tu sesión expiró o fue cerrada en otro dispositivo. Inicia sesión nuevamente.';

const STATUS_MESSAGES: Readonly<Record<number, string>> = {
  [HttpStatusCode.BadRequest]:
    'La solicitud contiene datos inválidos. Revisa la información e inténtalo de nuevo.',
  [HttpStatusCode.Unauthorized]: 'Tu sesión no es válida o ha expirado. Inicia sesión nuevamente.',
  [HttpStatusCode.Forbidden]: 'No tienes permisos para realizar esta acción.',
  [HttpStatusCode.NotFound]: 'No se encontró la información solicitada.',
  [HttpStatusCode.Conflict]:
    'La información genera un conflicto. Actualiza los datos e inténtalo de nuevo.',
  [HttpStatusCode.UnprocessableEntity]:
    'No se pudo procesar la información enviada. Revisa los campos.',
  [HttpStatusCode.TooManyRequests]:
    'Se realizaron demasiadas solicitudes. Espera un momento e inténtalo de nuevo.',
  [HttpStatusCode.InternalServerError]:
    'Ocurrió un error interno en el servidor. Inténtalo más tarde.',
  [HttpStatusCode.BadGateway]: 'El servicio no está disponible temporalmente. Inténtalo más tarde.',
  [HttpStatusCode.ServiceUnavailable]:
    'El servicio no está disponible temporalmente. Inténtalo más tarde.',
  [HttpStatusCode.GatewayTimeout]:
    'El servicio no está disponible temporalmente. Inténtalo más tarde.',
};

/**
 *
 * Función utilizada para notificar al usuario un mensaje según el código de estado cuando una petición HTTP falla y propagar el error
 * En un 401 de una sesión activa cierra la sesión local y redirige a /login (token expirado o revocado, p. ej. por inicio de sesión en otro equipo)
 */
export const errorStatusInterceptor: HttpInterceptorFn = (request, next) => {
  const notification = inject(Notification);
  const authSession = inject(AuthSessionService);
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        if (isSessionExpiredError(error, request.url, authSession.accessToken())) {
          authSession.logout();
          notification.error(SESSION_EXPIRED_MESSAGE, 6000);
          void router.navigate(['/login']);
        } else {
          notification.error(messageForStatus(error.status));
        }
      }

      return throwError(() => error);
    }),
  );
};

/**
 *
 * Función utilizada para determinar si un error 401 corresponde a una sesión activa que expiró o fue invalidada: solo aplica a los APIs
 * configurados (excluyendo el login) y cuando existe un token en memoria; el chequeo del token evita notificaciones duplicadas cuando
 * varias peticiones concurrentes fallan con 401 tras el primer cierre de sesión
 */
function isSessionExpiredError(
  error: HttpErrorResponse,
  requestUrl: string,
  accessToken: string | null,
): boolean {
  return (
    error.status === Number(HttpStatusCode.Unauthorized) &&
    accessToken !== null &&
    isConfiguredApiRequest(requestUrl) &&
    !requestUrl.includes(ROUTER_PATHS.LOGIN)
  );
}

/**
 *
 * Función utilizada para obtener el mensaje de error asociado al código de estado HTTP, o uno por defecto según sea error de servidor o de solicitud
 */
function messageForStatus(status: number): string {
  const message = STATUS_MESSAGES[status];
  if (message) {
    return message;
  }

  return status >= Number(HttpStatusCode.InternalServerError)
    ? DEFAULT_SERVER_ERROR_MESSAGE
    : DEFAULT_REQUEST_ERROR_MESSAGE;
}
